import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft,
  PaperPlaneRight,
  User,
  Clock,
  Ticket,
  EnvelopeSimple
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { getIssue, getEmailThread, sendEmail, getOrganization, Issue, EmailThread, Organization } from '../lib/api';

export default function IssueConversationPage() {
  const { issueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [issue, setIssue] = useState<Issue | null>(null);
  const [thread, setThread] = useState<EmailThread | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!issueId) return;
    
    const fetchData = async () => {
      try {
        // Fetch issue
        const issueRes = await getIssue(issueId);
        setIssue(issueRes.data);
        
        // Fetch email thread if exists
        if (issueRes.data?.email_thread_id) {
          const threadRes = await getEmailThread(issueRes.data.email_thread_id);
          setThread(threadRes.data);
        }
        
        // Fetch organization for email from_name
        if (user?.organization_id) {
          const orgRes = await getOrganization(user.organization_id);
          setOrganization(orgRes.data || orgRes as unknown as Organization || null);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Kunde inte ladda konversationen');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [issueId, user?.organization_id]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (thread?.messages) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [thread?.messages]);

  const handleReply = async () => {
    if (!replyText.trim() || !thread) return;
    
    // Find the recipient - reply to sender of last inbound, or original participants
    const lastInbound = [...(thread.messages || [])].reverse().find(m => m.direction === 'inbound');
    const toEmail = lastInbound?.from_email || issue?.reporter_email || thread.participants?.[0];
    
    if (!toEmail) {
      toast.error('Kunde inte hitta mottagare');
      return;
    }
    
    setSending(true);
    
    try {
      await sendEmail(toEmail, `Re: ${thread.subject}`, replyText, { fromName: organization?.name || 'Förvaltningen' });
      toast.success('Svar skickat');
      setReplyText('');
      // Refresh thread
      const threadRes = await getEmailThread(thread.id);
      setThread(threadRes.data);
    } catch (error) {
      toast.error('Ett fel uppstod');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('sv-SE');
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-32 bg-slate-200 rounded" />
          <div className="h-8 w-64 bg-slate-200 rounded" />
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="h-20 bg-slate-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-center">
        <p className="text-slate-500">Ärendet hittades inte</p>
        <Button variant="outline" onClick={() => navigate('/issues')} className="mt-4">
          Tillbaka till ärenden
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="issue-conversation-page">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-4 py-3">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Link
              to={`/issues/${issueId}`}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
            >
              <ArrowLeft size={14} weight="bold" />
              <span>Tillbaka till ärende</span>
            </Link>
          </div>
          
          <div className="flex items-center gap-3 mt-2">
            <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
              <EnvelopeSimple size={16} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-blue-600">{issue.issue_number}</span>
                <h1 className="text-sm font-semibold text-slate-900 truncate">{issue.title}</h1>
              </div>
              <p className="text-xs text-slate-500">
                {issue.reporter_name && <span>{issue.reporter_name} • </span>}
                {issue.reporter_email || thread?.participants?.[0] || 'Ingen mottagare'}
              </p>
            </div>
            <Link to={`/issues/${issueId}`}>
              <Button variant="outline" size="sm" className="h-7 text-xs">
                <Ticket size={14} className="mr-1" />
                Visa ärende
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        <div className="max-w-4xl mx-auto p-4 space-y-4">
          {/* Show issue description as first "message" */}
          <div className="flex justify-start">
            <div className="max-w-[80%] bg-white border border-slate-200 rounded-2xl rounded-tl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                  <User size={12} className="text-slate-500" />
                </div>
                <span className="text-xs font-medium text-slate-700">
                  {issue.reporter_name || 'Anmälare'}
                </span>
                <span className="text-[10px] text-slate-400">
                  {formatDate(issue.created_at)}
                </span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {issue.description}
              </p>
              {issue.source === 'email' && (
                <div className="mt-2 text-[10px] text-purple-600 flex items-center gap-1">
                  <EnvelopeSimple size={10} />
                  Ursprungligt e-postmeddelande
                </div>
              )}
            </div>
          </div>

          {/* Email thread messages - skip first inbound if it matches description */}
          {thread?.messages?.filter((msg, idx) => {
            // Skip first inbound message if it matches description
            if (idx === 0 && msg.direction === 'inbound' && issue?.description) {
              const descClean = issue.description.trim().substring(0, 100);
              const bodyClean = (msg.body || '').trim().substring(0, 100);
              if (descClean === bodyClean) {
                return false;
              }
            }
            return true;
          }).map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl p-4 shadow-sm ${
                  msg.direction === 'outbound'
                    ? 'bg-slate-800 text-white rounded-tr'
                    : 'bg-white border border-slate-200 rounded-tl'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    msg.direction === 'outbound' ? 'bg-slate-700' : 'bg-slate-100'
                  }`}>
                    <User size={12} className={msg.direction === 'outbound' ? 'text-slate-300' : 'text-slate-500'} />
                  </div>
                  <span className={`text-xs font-medium ${
                    msg.direction === 'outbound' ? 'text-slate-300' : 'text-slate-700'
                  }`}>
                    {msg.from_email?.split('@')[0] || 'Okänd'}
                  </span>
                  <span className={`text-[10px] ${
                    msg.direction === 'outbound' ? 'text-slate-500' : 'text-slate-400'
                  }`}>
                    {formatDate(msg.created_at)}
                  </span>
                </div>
                <p className={`text-sm whitespace-pre-wrap leading-relaxed ${
                  msg.direction === 'outbound' ? 'text-white' : 'text-slate-700'
                }`}>
                  {msg.body}
                </p>
              </div>
            </div>
          ))}

          {/* No messages state */}
          {(!thread || !thread.messages?.length) && (
            <div className="text-center py-8">
              <EnvelopeSimple size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">Ingen e-postkonversation ännu</p>
              <p className="text-xs text-slate-400 mt-1">Skriv ett meddelande nedan för att starta</p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Reply input */}
      <div className="border-t border-slate-200 bg-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-3">
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={thread ? "Skriv ett svar..." : "Skriv ett meddelande för att starta konversationen..."}
              rows={2}
              className="flex-1 resize-none text-sm min-h-[60px]"
              data-testid="conversation-reply-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  handleReply();
                }
              }}
            />
            <Button
              onClick={handleReply}
              disabled={!replyText.trim() || sending || !thread}
              className="self-end h-10 px-4"
              data-testid="send-conversation-reply"
            >
              <PaperPlaneRight size={18} />
            </Button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-[10px] text-slate-400">
              ⌘/Ctrl+Enter för att skicka
            </p>
            {issue.reporter_email && (
              <p className="text-[10px] text-slate-400">
                Skickas till: {issue.reporter_email}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
